import { decodeMapMesh, type MapMesh } from "@69ly/maps";
import type { MapMeshInfo } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gunzip } from "../demo/unpack.ts";
import { api, ApiFailure } from "./api.ts";

const keys = {
  list: ["map-meshes"] as const,
  one: (id: string) => ["map-meshes", "file", id] as const,
};

/** Which maps have a 3D mesh. */
export const useMapMeshes = () => useQuery({ queryKey: keys.list, queryFn: () => api<MapMeshInfo[]>("GET", "/maps/meshes"), staleTime: 5 * 60_000 });

/** A map's mesh, downloaded and read; an upload gets a new id, so a mesh never changes. */
export function useMapMesh(info: MapMeshInfo | undefined) {
  return useQuery({
    queryKey: keys.one(info?.id ?? ""),
    enabled: !!info,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    queryFn: async (): Promise<MapMesh> => {
      const res = await fetch(info!.url, { credentials: "same-origin" });
      if (!res.ok) throw new ApiFailure(res.status, res.status === 404 ? "not_found" : "internal", res.statusText);
      return decodeMapMesh(await gunzip(await res.blob()));
    },
  });
}

export function useUploadMesh() {
  const qc = useQueryClient();
  return useMutation({
    /** `textured`: the map as it looks (.textured.glb); otherwise its mesh (.mesh.bin.gz). */
    mutationFn: async ({ map, file, kind = "mesh" }: { map: string; file: File; kind?: "mesh" | "textured" }) => {
      const form = new FormData();
      form.append(kind, file, file.name);
      let res: Response;
      try {
        res = await fetch(`/api/admin/maps/${map}/${kind}`, { method: "PUT", credentials: "same-origin", body: form });
      } catch {
        throw new ApiFailure(0, "network", "Network error");
      }
      const data = (await res.json().catch(() => null)) as (MapMeshInfo[] & { code?: ApiFailure["code"]; message?: string }) | null;
      if (!res.ok) throw new ApiFailure(res.status, data?.code ?? "internal", data?.message ?? res.statusText);
      return data as MapMeshInfo[];
    },
    onSuccess: (list) => qc.setQueryData(keys.list, list),
  });
}

export function useDeleteMesh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ map, kind = "mesh" }: { map: string; kind?: "mesh" | "textured" }) => api<MapMeshInfo[]>("DELETE", `/admin/maps/${map}/${kind}`),
    onSuccess: (list) => qc.setQueryData(keys.list, list),
  });
}
